// Adapted from eval/dashboard/internal_web/src/components/ui/Acuity.tsx (ATM-57): no truth → said pair; an off-scale value is an em dash. Keep in step until ATM-60 shares it.
/**
 * An acuity, as an outlined pill on the four-level severity ramp (the eval dashboard's
 * `Acuity`, without its truth → said pair, which needs a ground truth).
 *
 * A value the four-level scale cannot place — `unknown`, a session with no report yet —
 * renders as an em dash, never coerced to `non_urgent`: that coercion would invent a
 * severity nobody assessed.
 */

import { cn } from "../../lib/cn";
import { EM_DASH, urgencyLabel } from "../../lib/format";
import { acuityTone } from "../../lib/urgency";
import type { Urgency } from "../../api/types";

const TONE_CLASS: Record<Urgency, string> = {
  non_urgent: "acuity-non-urgent",
  semi_urgent: "acuity-semi-urgent",
  urgent: "acuity-urgent",
  emergency: "acuity-emergency",
};

type Props = {
  /** The raw wire value: one of the four levels, `unknown`, or `null`. */
  urgency: string | null;
  /** Hover text for the em dash, saying why there is no level yet. */
  missingTitle?: string;
  className?: string;
};

/** One acuity pill, or an em dash where no level applies. */
export default function Acuity({ urgency, missingTitle, className }: Props) {
  const tone = acuityTone(urgency);
  if (tone === null) {
    return (
      <span className={cn("text-cell text-ink-faint", className)} title={missingTitle}>
        {EM_DASH}
      </span>
    );
  }
  return <span className={cn("acuity", TONE_CLASS[tone], className)}>{urgencyLabel(tone)}</span>;
}
